import {useEffect, useState, FormEvent} from 'react';
import Head from 'next/head';
import Script from 'next/script';
import {useRouter} from 'next/router';
import {useToast} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';

import {SendResetCode, EnterResetCode, ResetPasswordForm} from '@eksab/features/auth/components';
import {useForgetPasswordMutation, useResetPasswordMutation} from '@eksab/features/auth/hooks';
import type {OTP} from '@eksab/features/auth/types';
import {config} from '@eksab/config';
import {GetStaticProps} from 'next';
import {dehydrate, QueryClient} from '@tanstack/react-query';
import {setApiLocale} from '@eksab/api';
import {useRegionSettings} from '@eksab/hooks/useRegionSettings';

export default function ResetPassword() {
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState<OTP>(['', '', '', '', '', '']);
  const [showEnterResetCode, setShowEnterResetCode] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const router = useRouter();
  const toast = useToast();

  const t = useTranslate();
  const forgetPasswordMutation = useForgetPasswordMutation();
  const resetPasswordMutation = useResetPasswordMutation();

  useEffect(() => {
    const otp = router.query.otp;
    const identifier = router.query.identifier;

    if (otp && identifier) {
      setIdentifier(identifier as string);
      setShowEnterResetCode(true);
    }
  }, [router.query]);

  const onResetPasswordFormBack = () => {
    setShowResetForm(false);
    setShowEnterResetCode(true);
  };

  const onResetPasswordFormSubmit = (new_password: string) =>
    resetPasswordMutation.mutate(
      {otp: otp.join(''), identifier, new_password},
      {
        onSuccess: () => {
          toast({title: t('auth.reset_password_successfully'), status: 'success'});
          router.push('/login');
        },
        onError: (e) => void toast({title: e.response?.data?.message ?? t('app.errors.try-again'), status: 'error'}),
      },
    );

  const onSubmitSendResetCode = (identifier: string) => (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    window.grecaptcha?.ready(() =>
      window.grecaptcha!.execute(config.recaptchaSiteKey!, {action: 'submit'}).then((token) => {
        forgetPasswordMutation.mutate(
          {identifier, recaptcha: token},
          {
            onSuccess: () => {
              toast({title: t('auth.otp_sent'), status: 'success'});
              setShowEnterResetCode(true);
            },
            onError: (e) =>
              void toast({title: e.response?.data?.message ?? t('app.errors.try-again'), status: 'error'}),
          },
        );
      }),
    );
  };

  const onVerifyClicked = () => {
    setShowResetForm(true);
    setShowEnterResetCode(false);
  };

  return (
    <>
      <Head>
        <meta name="description" content="Predict game score and win prizes | Eksab" />
        <title>{`${t('auth.reset_password')} | ${t('app.name')}`}</title>
      </Head>

      <Script src={`https://www.google.com/recaptcha/api.js?render=${config.recaptchaSiteKey}`} strategy="lazyOnload" />

      {showResetForm ? (
        <ResetPasswordForm
          goBack={onResetPasswordFormBack}
          onSubmit={onResetPasswordFormSubmit}
          isLoading={resetPasswordMutation.isLoading}
        />
      ) : showEnterResetCode ? (
        <EnterResetCode
          otp={otp}
          setOtp={setOtp}
          goBack={() => {
            setIdentifier('');
            setShowEnterResetCode(false);
          }}
          onVerifyClicked={onVerifyClicked}
        />
      ) : (
        <SendResetCode
          onSubmit={onSubmitSendResetCode}
          identifier={identifier}
          setIdentifier={setIdentifier}
          isLoading={forgetPasswordMutation.isLoading}
        />
      )}
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({locale}) => {
  const queryClient = new QueryClient();

  setApiLocale(locale);

  await queryClient.prefetchQuery([useRegionSettings.queryKey, locale], useRegionSettings.queryFn);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 60 * 60 * 3,
  };
};
